import React, { useState, useRef, useMemo, useCallback } from 'react'
import {
  Container,
  Header,
  Button,
  ButtonDropdown,
  SpaceBetween,
  Alert,
  Box,
  ExpandableSection,
  FormField,
  Select,
  Input,
  Toggle
} from '@cloudscape-design/components'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import { Dashboard } from '../types'
import { DashboardCanvas } from './DashboardCanvas'
import { DashboardControls } from './DashboardControls'
import { DashboardProvider, DEFAULT_TIME_RANGES, type TimeRange } from '../contexts/DashboardContext'
import { DashboardPermissions } from './DashboardPermissions'

interface DashboardBuilderProps {
  dashboard?: Dashboard
  isEditing?: boolean
  onSave?: (dashboard: Dashboard) => void
  onCancel?: () => void
}

// Helper function to convert string time range to TimeRange object
const convertStringToTimeRange = (timeRangeString: string): TimeRange => {
  const timeRangeMap: Record<string, TimeRange> = {
    // Full format
    'last-5-minutes': DEFAULT_TIME_RANGES[0],
    'last-15-minutes': DEFAULT_TIME_RANGES[1], 
    'last-30-minutes': DEFAULT_TIME_RANGES[2],
    'last-1-hour': DEFAULT_TIME_RANGES[3],
    'last-6-hours': DEFAULT_TIME_RANGES[4],
    'last-12-hours': DEFAULT_TIME_RANGES[5],
    'last-24-hours': DEFAULT_TIME_RANGES[6],
    'last-7-days': DEFAULT_TIME_RANGES[7],
    'last-30-days': DEFAULT_TIME_RANGES[8],
    // Short format (stored in database)
    '5m': DEFAULT_TIME_RANGES[0],
    '15m': DEFAULT_TIME_RANGES[1],
    '30m': DEFAULT_TIME_RANGES[2],
    '1h': DEFAULT_TIME_RANGES[3],
    '6h': DEFAULT_TIME_RANGES[4],
    '12h': DEFAULT_TIME_RANGES[5],
    '24h': DEFAULT_TIME_RANGES[6],
    '7d': DEFAULT_TIME_RANGES[7],
    '30d': DEFAULT_TIME_RANGES[8]
  }
  
  return timeRangeMap[timeRangeString] || DEFAULT_TIME_RANGES[6] // Default to last 24 hours
}

export const DashboardBuilder: React.FC<DashboardBuilderProps> = ({
  dashboard,
  isEditing = false,
  onSave,
  onCancel
}) => {
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(dashboard || null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Clear page header to avoid duplicate titles
  const { setHeaderConfig } = usePageHeader()
  
  React.useEffect(() => {
    setHeaderConfig({
      title: '',
      description: '',
      actions: []
    })
  }, [])

  // Define handler functions with useCallback to prevent re-renders
  const handleSave = useCallback(async () => {
    if (!currentDashboard) return
    
    try {
      setError(null)
      if (onSave) {
        await onSave(currentDashboard)
        setHasUnsavedChanges(false)
      }
    } catch (err: any) {
      setError(`Failed to save dashboard: ${err.message}`)
    }
  }, [currentDashboard, onSave])

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      // Could add a confirmation modal here
      if (!window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return
      }
    }
    if (onCancel) {
      onCancel()
    }
  }, [hasUnsavedChanges, onCancel])
  

  const handleDashboardChange = useCallback((updatedDashboard: Dashboard) => {
    setCurrentDashboard(updatedDashboard)
    setHasUnsavedChanges(true)
  }, [])

  const handleAddWidget = useCallback((widgetType: string) => {
    if (!currentDashboard) {
      // Create a new dashboard if none exists
      const newDashboard: Dashboard = {
        _id: '' as any, // Will be set when saved
        name: 'New Dashboard',
        description: '',
        owner_id: '' as any, // Will be set from user context
        is_public: false,
        layout: [],
        widgets: [],
        default_time_range: 'last-1-hour',
        refresh_interval: 300 // 5 minutes
      }
      setCurrentDashboard(newDashboard)
    }
    
    // Create the new widget based on type
    if (widgetType === 'chart-widget') {
      const newWidget = {
        id: `chart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'chart' as const,
        title: 'Time Series Chart',
        description: 'Real-time data visualization',
        queries: [],
        visualization: {
          chartType: 'line',
          xAxis: {
            type: 'time',
            label: 'Time',
            timeZone: 'UTC'
          },
          yAxes: [{
            id: 'left',
            label: 'Value',
            unit: ''
          }],
          colors: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'],
          legend: {
            show: true,
            position: 'bottom'
          },
          tooltip: {
            show: true
          },
          zoom: {
            enabled: true,
            resetOnNewData: false
          },
          interpolateNulls: false,
          showDataPoints: false,
          connectNulls: false
        },
        layout: {
          i: `chart_${Date.now()}`,
          x: 0,
          y: 0,
          w: 2,  // 2 columns wide (1/6 of 12-column grid)
          h: 2   // 2 rows tall (very compact)
        },
        refreshInterval: 30000,
        timeRange: {
          type: 'relative',
          relative: {
            amount: 1,
            unit: 'hour'
          }
        },
        enabled: true
      }

      const updatedDashboard = {
        ...currentDashboard,
        widgets: [...currentDashboard.widgets, newWidget],
        layout: [...currentDashboard.layout, newWidget.layout]
      }

      setCurrentDashboard(updatedDashboard)
      setHasUnsavedChanges(true)
    } else {
    }
  }, [currentDashboard])

  return (
    <DashboardProvider
      initialTimeRange={currentDashboard?.default_time_range ? convertStringToTimeRange(currentDashboard.default_time_range) : undefined}
    >
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Error Alert */}
        {error && (
          <Alert
            type="error"
            statusIconAriaLabel="Error"
            header="Dashboard Error"
            dismissible
            onDismiss={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <div style={{ display: 'flex', flex: 1 }}>
          {/* Main Dashboard Canvas Area */}
          <div style={{ 
            flex: 1,
            overflow: 'auto',
            padding: '8px'
          }}>
            {/* Compact Header with Controls in One Row */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
              padding: '8px 16px',
              backgroundColor: '#fafbfc',
              borderRadius: '8px',
              border: '1px solid #e9ebed'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div>
                  <h1 style={{ margin: '0', fontSize: '20px', fontWeight: '500' }}>
                    {isEditing ? `Edit: ${currentDashboard?.name || 'Untitled'}` : 'Create New Dashboard'}
                  </h1>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DashboardControls />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ButtonDropdown
                  items={[
                    {
                      id: 'chart-widget',
                      text: 'Time Series Chart',
                      iconName: 'line-chart'
                    },
                    {
                      id: 'kpi-widget', 
                      text: 'KPI Widget',
                      iconName: 'status-info'
                    },
                    {
                      id: 'table-widget',
                      text: 'Data Table', 
                      iconName: 'table'
                    }
                  ]}
                  onItemClick={({ detail }) => handleAddWidget(detail.id)}
                  variant="normal"
                  iconName="add-plus"
                  ariaLabel="Add widget"
                />
                <Button 
                  onClick={handleCancel}
                  variant="icon"
                  iconName="close"
                  ariaLabel="Cancel"
                />
                <Button 
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || !currentDashboard}
                  variant="icon"
                  iconName="check"
                  ariaLabel={isEditing ? 'Save changes' : 'Create dashboard'}
                />
              </div>
            </div>

            {/* Dashboard Settings */}
            {currentDashboard && (
              <Box margin={{ bottom: 'm' }}>
                <ExpandableSection
                  headerText="Dashboard Settings"
                  variant="container"
                >
                  <SpaceBetween direction="vertical" size="m">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                      <FormField label="Dashboard Name">
                        <Input
                          value={currentDashboard.name}
                          onChange={({ detail }) => {
                            const updated = { ...currentDashboard, name: detail.value }
                            setCurrentDashboard(updated)
                            setHasUnsavedChanges(true)
                          }}
                          placeholder="Enter dashboard name"
                        />
                      </FormField>

                      <FormField label="Description">
                        <Input
                          value={currentDashboard.description || ''}
                          onChange={({ detail }) => {
                            const updated = { ...currentDashboard, description: detail.value }
                            setCurrentDashboard(updated)
                            setHasUnsavedChanges(true)
                          }}
                          placeholder="Enter description"
                        />
                      </FormField>

                      <FormField label="Default Time Range">
                        <Select
                          selectedOption={{ 
                            label: convertStringToTimeRange(currentDashboard.default_time_range).relative?.label || 'Last 1 hour',
                            value: currentDashboard.default_time_range 
                          }}
                          onChange={({ detail }) => {
                            const updated = { ...currentDashboard, default_time_range: detail.selectedOption.value }
                            setCurrentDashboard(updated)
                            setHasUnsavedChanges(true)
                          }}
                          options={[
                            { label: 'Last 5 minutes', value: '5m' },
                            { label: 'Last 15 minutes', value: '15m' },
                            { label: 'Last 30 minutes', value: '30m' },
                            { label: 'Last 1 hour', value: '1h' },
                            { label: 'Last 6 hours', value: '6h' },
                            { label: 'Last 12 hours', value: '12h' },
                            { label: 'Last 24 hours', value: '24h' },
                            { label: 'Last 7 days', value: '7d' },
                            { label: 'Last 30 days', value: '30d' }
                          ]}
                          placeholder="Select default time range"
                        />
                      </FormField>

                      <FormField label="Auto-refresh Interval">
                        <Select
                          selectedOption={{ 
                            label: currentDashboard.refresh_interval === 0 ? 'Off' : 
                                   currentDashboard.refresh_interval < 60000 ? `${currentDashboard.refresh_interval / 1000}s` :
                                   currentDashboard.refresh_interval < 3600000 ? `${currentDashboard.refresh_interval / 60000}m` :
                                   `${currentDashboard.refresh_interval / 3600000}h`,
                            value: currentDashboard.refresh_interval.toString()
                          }}
                          onChange={({ detail }) => {
                            const updated = { ...currentDashboard, refresh_interval: parseInt(detail.selectedOption.value) }
                            setCurrentDashboard(updated)
                            setHasUnsavedChanges(true)
                          }}
                          options={[
                            { label: 'Off', value: '0' },
                            { label: '5s', value: '5000' },
                            { label: '10s', value: '10000' },
                            { label: '30s', value: '30000' },
                            { label: '1m', value: '60000' },
                            { label: '5m', value: '300000' },
                            { label: '15m', value: '900000' },
                            { label: '30m', value: '1800000' },
                            { label: '1h', value: '3600000' }
                          ]}
                          placeholder="Select refresh interval"
                        />
                      </FormField>

                      <FormField label="Public Dashboard">
                        <Toggle
                          checked={currentDashboard.is_public}
                          onChange={({ detail }) => {
                            const updated = { ...currentDashboard, is_public: detail.checked }
                            setCurrentDashboard(updated)
                            setHasUnsavedChanges(true)
                          }}
                        />
                      </FormField>
                    </div>

                    {/* Dashboard Permissions - Only show for existing dashboards */}
                    {isEditing && (
                      <Box margin={{ top: 'l' }}>
                        <DashboardPermissions
                          dashboardId={currentDashboard._id}
                          currentPermissions={currentDashboard.permissions}
                          isPublic={currentDashboard.is_public}
                          onPermissionsUpdated={() => {
                            // Optionally refresh the dashboard data or show a success message
                            setHasUnsavedChanges(false)
                          }}
                        />
                      </Box>
                    )}
                  </SpaceBetween>
                </ExpandableSection>
              </Box>
            )}

            {/* Dashboard Canvas */}
            {currentDashboard && (
              <DashboardCanvas
                dashboard={currentDashboard}
                onChange={handleDashboardChange}
                isEditing={true}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardProvider>
  )
}